import { useEffect, useState } from 'react'

import { useClearUserMetadataFromHash, useFirebase } from '@scouts/auth'
import { getExpoPushToken } from '@scouts/helpers'
import { colors, Loadable } from '@scouts/ui'

import { authenticate } from '@/actions'
import { initSentryUser } from '@/helpers/sentry'
import { useAppDispatch } from '@/store'
import { useMobilePushNotifications } from '@/store/push-notifications'
import { useEventTracking, TrackingEvent } from '@/telemetry'
import { track, TRACK_USER_AUTHENTICATE, TRACK_USER_SIGNUP } from '@/trackers'

import { useIntercomActions } from '@/hooks/use-intercom-actions'

import { useAuthenticationProfileData } from './hooks/use-authentication-profile-data'
import { useResetSessionAndLogout } from './hooks/use-reset-session-and-logout'

import { AuthenticationContainer } from './AuthenticationContainer'

const useAuthentication = () => {
    const dispatch = useAppDispatch()

    const { capture, identify } = useEventTracking()

    const { user, isInitialized } = useFirebase()
    const { resetSessionAndLogout } = useResetSessionAndLogout()

    const [shouldAttemptAuthentication, setShouldAttemptAuthentication] = useState(true)
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const { profileData } = useAuthenticationProfileData()
    const { updateIntercomProfile } = useIntercomActions()
    const { clearUserMetadataFromHash } = useClearUserMetadataFromHash()

    const { enablePushNotifications } = useMobilePushNotifications()

    useEffect(() => {
        // Do nothing if Firebase is not initialized

        if (!isInitialized) return

        // Reset state if the user is not logged in with Firebase

        if (!user) {
            setIsAuthenticated(false)
            setShouldAttemptAuthentication(true)
            return
        }

        // Skip authentication against API if we've already tried

        if (!shouldAttemptAuthentication) return

        // Attempt authentication against API

        setIsLoading(true)
        setShouldAttemptAuthentication(false)

        dispatch(authenticate(profileData))
            .then((result) => {
                clearUserMetadataFromHash()

                const profile = result.profile

                setIsAuthenticated(true)

                track(TRACK_USER_AUTHENTICATE, {
                    email: profile.email,
                    initialVariant: profile.initialVariant,
                    languagePreference: profile.languagePreference,
                    referralSource: profile.referralSource,
                    userId: profile.id,
                    variant: profile.variant,
                })

                if (profile?.id) {
                    initSentryUser(profile)

                    identify(profile.id, {
                        initial_variant: profile.initialVariant || null,
                        referral_source: profile.referralSource || null,
                        language_preference: profile.languagePreference || null,
                        ...(result.isSignUp
                            ? {
                                  cookie_utm_campaign: result.extraCookieData.marketingData.utmCampaign || null,
                                  cookie_utm_content: result.extraCookieData.marketingData.utmContent || null,
                                  cookie_utm_medium: result.extraCookieData.marketingData.utmMedium || null,
                                  cookie_utm_source: result.extraCookieData.marketingData.utmSource || null,
                                  cookie_utm_term: result.extraCookieData.marketingData.utmTerm || null,
                              }
                            : {}),
                    })
                }

                if (result.isSignUp) {
                    updateIntercomProfile({ initial_variant: profile.variant })

                    track(TRACK_USER_SIGNUP, {
                        email: profile.email,
                        initialVariant: profile.initialVariant,
                        languagePreference: profile.languagePreference,
                        referralSource: profile.referralSource,
                        userId: profile.id,
                        variant: profile.variant,
                    })

                    capture(TrackingEvent.UserSignedUp, {
                        hashed_email: result.hashedEmail,
                        referral_source: profile.referralSource,
                        language_preference: profile.languagePreference || null,
                        variant: profile.variant,
                    })
                }

                const expoPushToken = getExpoPushToken()

                if (expoPushToken?.includes('ExponentPushToken[')) {
                    enablePushNotifications({ token: expoPushToken })
                }
            })
            .catch(() => {
                setIsAuthenticated(false)
                resetSessionAndLogout()
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [
        capture,
        clearUserMetadataFromHash,
        dispatch,
        enablePushNotifications,
        identify,
        isInitialized,
        profileData,
        resetSessionAndLogout,
        shouldAttemptAuthentication,
        updateIntercomProfile,
        user,
    ])

    return { isAuthenticated, isLoading, isInitialized, user }
}

export const AuthenticationBoundary = ({ children }: { children: React.ReactNode }) => {
    const { isAuthenticated, isLoading, isInitialized, user } = useAuthentication()

    // #1 Render loading state while Firebase is initializing

    if (!isInitialized) {
        // Spinners in this component have different colours to observe the loading state of the app – we can remove this down the line
        return <Loadable isLoading isFullViewportHeight />
    }

    // #2 Render login/signup/reset password pages if not authenticated with Firebase

    if (!user) return <AuthenticationContainer />

    // #3 Render loading state while we check if the user is authenticated against our API

    if (isLoading) {
        return <Loadable isLoading isFullViewportHeight />
    }

    // #4 Render children if the user is authenticated against our API

    if (isAuthenticated) {
        return <>{children}</>
    }

    // #5 Render loading state for any other case as a fallback

    return <Loadable spinnerColor={colors.surface.alert} isLoading isFullViewportHeight />
}
